import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Phone,
  Users,
  CalendarDays,
  PhoneCall,
  Target,
  Handshake,
  TrendingUp,
  ArrowUpDown,
  DollarSign,
  Wallet,
  Percent,
  BarChart3,
  ShoppingCart,
  Calculator,
  MousePointerClick,
  XCircle,
  FileText,
  Calendar as CalendarIcon,
  Search,
  Sparkles,
} from "lucide-react";

import OnboardingBanner from "@/components/OnboardingBanner";
import OnboardingResumeBanner from "@/components/OnboardingResumeBanner";
import { OnboardingCompletionWidget } from "@/components/OnboardingCompletionWidget";
import { TeamHealthScores } from "@/components/TeamHealthScores";
import { RoutingActivityWidget } from "./RoutingActivityWidget";
import { DailyReportDialog } from "./DailyReportDialog";
import { MyRankTrendBanner } from "./MyRankTrendBanner";
import { KpiCard } from "./KpiCard";
import { SyncPill } from "@/components/SyncPill";
import { StreakLeadersWidget } from "./StreakLeadersWidget";
import { WinsReelWidget } from "./WinsReelWidget";
import { GrowthDigestWidget } from "./GrowthDigestWidget";
import { PaymentSourcesWidget } from "./PaymentSourcesWidget";
import { StreakRepliesWidget } from "./StreakRepliesWidget";
import { useAccountDateRange } from "@/hooks/useAccountDateRange";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const kpiConfig = [
  { key: "dials", label: "DIALS", icon: Phone, format: "number" },
  { key: "connects", label: "CONNECTS", icon: Users, format: "number" },
  { key: "sets_booked", label: "SETS BOOKED", icon: CalendarDays, format: "number" },
  { key: "calls_taken", label: "CALLS TAKEN", icon: PhoneCall, format: "number" },
  { key: "shows", label: "SHOWS", icon: Target, format: "number", color: "text-green-400" },
  { key: "deals_closed", label: "DEALS CLOSED", icon: Handshake, format: "number" },
  {
    key: "revenue_generated", label: "BOOKED REVENUE", icon: TrendingUp, format: "currency",
    // Iter184 (A02) — carry the definition on hover so managers stop
    // reading two dollar values as one duplicated number.
    tooltip: "Total contract value booked in the period (may include payment plans not yet fully collected).",
  },
  {
    key: "cash_collected", label: "CASH COLLECTED", icon: DollarSign, format: "currency",
    subtext: "From closers only",
    tooltip: "Money actually received. Lower than Booked Revenue when deals sit on payment plans.",
  },
  { key: "setter_cash", label: "SETTER CASH", icon: Wallet, format: "currency", subtext: "Personal commissions" },
  { key: "show_rate", label: "SHOW RATE", icon: Percent, format: "percent", subtext: "No Shows / Calls Taken" },
  { key: "close_rate", label: "CLOSE RATE", icon: Percent, format: "percent", subtext: "Deals Closed / Shows" },
  { key: "connect_to_set", label: "CONNECT-TO-SET", icon: BarChart3, format: "percent", subtext: "Sets Booked / Connects" },
  { key: "aov", label: "AOV", icon: ShoppingCart, format: "currency", subtext: "Cash Collected / Deals Closed" },
  { key: "cash_per_call", label: "CASH PER CALL", icon: Calculator, format: "currency", subtext: "Custom formula" },
  { key: "inbound_ctas", label: "INBOUND CTA'S", icon: MousePointerClick, format: "number" },
  { key: "dqs", label: "DQ'S", icon: XCircle, format: "number" },
];

export default function ManagerView() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  // Iter184 (B04) — shared account-level date range across pages.
  const [dateRange, setDateRange] = useAccountDateRange();
  const [filters, setFilters] = useState({
    team_id: "",
    user_id: "",
    start_date: format(dateRange.from, "yyyy-MM-dd"),
    end_date: format(dateRange.to || dateRange.from, "yyyy-MM-dd"),
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [dailyReportOpen, setDailyReportOpen] = useState(false);
  const [compareOn, setCompareOn] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  // Sync the persisted date range into the filters shape the stats
  // endpoint expects. Any other page that changes the range (Deals,
  // Commissions) will fire the storage event and land here too.
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      start_date: format(dateRange.from, "yyyy-MM-dd"),
      end_date: format(dateRange.to || dateRange.from, "yyyy-MM-dd"),
    }));
  }, [dateRange]);

  const fetchTeamsAndUsers = useCallback(async () => {
    try {
      const [teamsRes, usersRes, leaderboardRes] = await Promise.all([
        fetch(`${API}/teams`, { credentials: "include" }),
        fetch(`${API}/users`, { credentials: "include" }),
        fetch(`${API}/leaderboard`, { credentials: "include" }),
      ]);
      if (teamsRes.ok) setTeams(await teamsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (leaderboardRes.ok) setLeaderboard(await leaderboardRes.json());
    } catch (error) {
      console.error("Failed to fetch teams/users:", error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.team_id) params.append("team_id", filters.team_id);
      if (filters.user_id) params.append("user_id", filters.user_id);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (compareOn) params.append("compare", "true");
      const response = await fetch(`${API}/dashboard/stats?${params}`, { credentials: "include" });
      if (response.ok) {
        setStats(await response.json());
        setLastSyncedAt(Date.now());
      }
    } catch (error) {
      console.error("Failed to fetch dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  }, [filters, compareOn]);

  const handleRefresh = () => {
    fetchData();
    fetchTeamsAndUsers();
  };

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchTeamsAndUsers(); }, [fetchTeamsAndUsers]);

  const handleDateSelect = (range) => {
    if (range?.from) {
      setDateRange(range);
      setFilters({
        ...filters,
        start_date: format(range.from, "yyyy-MM-dd"),
        end_date: range.to ? format(range.to, "yyyy-MM-dd") : format(range.from, "yyyy-MM-dd"),
      });
    }
  };

  const currentTeam = teams.find(t => t.team_id === filters.team_id);
  const topPerformer = leaderboard[0];

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="manager-dashboard-view">
      <OnboardingResumeBanner />
      <OnboardingBanner />
      <MyRankTrendBanner />

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold text-white font-heading">
            {currentTeam?.name ? `${currentTeam.name} Team` : "Team"} Dashboard
          </h1>
          <p className="text-gray-400 mt-1 text-xs sm:text-sm">Overview for selected period</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <SyncPill
            lastSyncedAt={lastSyncedAt}
            loading={loading}
            onRefresh={handleRefresh}
            testId="dashboard-sync-pill"
          />
          <Button
            variant={compareOn ? "default" : "outline"}
            data-testid="dashboard-compare-toggle"
            onClick={() => setCompareOn(!compareOn)}
            className={compareOn ? "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-500" : "bg-white/5 border-white/10 text-white hover:bg-white/10"}
          >
            <ArrowUpDown className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{compareOn ? "vs Prev Period" : "Compare"}</span>
            <span className="sm:hidden">{compareOn ? "On" : "Cmp"}</span>
          </Button>
          <Button variant="outline" data-testid="daily-report-btn" onClick={() => setDailyReportOpen(true)} className="bg-white/5 border-white/10 text-white hover:bg-white/10">
            <FileText className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Daily Report</span>
          </Button>
          <Button data-testid="ask-warmap-header-btn" className="hidden md:flex bg-gradient-to-r from-emerald-800 to-emerald-600 text-white neon-glow">
            <Sparkles className="w-4 h-4 mr-2" />
            Ask WarMap
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="date-range-btn" className="bg-white/5 border-white/10 text-white hover:bg-white/10 text-xs sm:text-sm">
                <CalendarIcon className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{format(dateRange.from, "MMM d")} - {dateRange.to ? format(dateRange.to, "MMM d, yyyy") : ""}</span>
                <span className="sm:hidden">{format(dateRange.from, "MM/dd")} - {dateRange.to ? format(dateRange.to, "MM/dd") : ""}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-[#1A1A1A] border-white/10" align="end">
              <Calendar mode="range" selected={dateRange} onSelect={handleDateSelect} numberOfMonths={1} className="bg-[#1A1A1A] text-white" />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 sm:gap-4 pb-4 mb-6">
        <Select value={filters.user_id || "all"} onValueChange={(v) => setFilters({ ...filters, user_id: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="rep-filter" className="w-28 sm:w-40 bg-white/5 border-white/10 text-white text-sm"><SelectValue placeholder="All Reps" /></SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-white/10">
            <SelectItem value="all" className="text-white">All Reps</SelectItem>
            {users.map((u) => <SelectItem key={u.user_id} value={u.user_id} className="text-white">{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.team_id || "all"} onValueChange={(v) => setFilters({ ...filters, team_id: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="role-filter" className="w-28 sm:w-40 bg-white/5 border-white/10 text-white text-sm"><SelectValue placeholder="All Roles" /></SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-white/10">
            <SelectItem value="all" className="text-white">All Roles</SelectItem>
            <SelectItem value="Closer" className="text-white">Closer</SelectItem>
            <SelectItem value="Setter" className="text-white">Setter</SelectItem>
            <SelectItem value="DM Setter" className="text-white">DM Setter</SelectItem>
          </SelectContent>
        </Select>
        <span className="hidden sm:block text-sm text-gray-500">Showing your data</span>
        <div className="w-full sm:w-auto sm:flex-1 sm:max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input data-testid="search-metrics" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 text-sm" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
          {[...Array(15)].map((_, i) => (
            <div key={`mgr-kpi-skel-${i}`} className="bg-[#1A1A1A] border border-white/5 rounded-xl p-5 animate-pulse">
              <div className="h-4 w-20 bg-white/10 rounded mb-4" />
              <div className="h-10 w-24 bg-white/10 rounded" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
          {kpiConfig
            .filter((kpi) => searchQuery ? kpi.label.toLowerCase().includes(searchQuery.toLowerCase()) : true)
            .map((kpi) => (
              <KpiCard
                key={kpi.key}
                label={kpi.label}
                value={stats?.[kpi.key]}
                icon={kpi.icon}
                format={kpi.format}
                subtext={kpi.subtext}
                color={kpi.color}
                tooltip={kpi.tooltip}
                compareOn={compareOn && stats?.compare}
                change={stats?.changes?.[kpi.key]}
                prevValue={stats?.previous?.[kpi.key]}
              />
            ))}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <TeamHealthScores />
        <RoutingActivityWidget />
      </div>
      <StreakLeadersWidget />
      <WinsReelWidget />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <StreakRepliesWidget />
        <GrowthDigestWidget />
      </div>
      <PaymentSourcesWidget />
      <OnboardingCompletionWidget />
      <DailyReportDialog open={dailyReportOpen} onOpenChange={setDailyReportOpen} stats={stats} topPerformer={topPerformer} />
    </div>
  );
}
